import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, TextInput } from '@leverege/ui-elements'
import { Select, UI } from '@leverege/ui-redux'
import { 
  Map,
  MapStyle, 
  SymbolLayer, 
  PolylineLayer,
  Symbolizer, 
  Symbols, 
  DrawControl,
  ReduxMapOptions,
  GeoshapeController,
  MapControl } from '@leverege/ui-mapbox-elements'

import SymbolSet from './SymbolSet'
import style from './style.css'
import LabelGroup from '../../views/LabelGroup';

const Path = require( '@leverege/path' )
const { Constants : ReduxConstants } = require( '@leverege/api-redux' )
const classnames = require( 'classnames' )

const MAP_NAME = 'map'

class MapResults extends React.Component {

  static contextTypes = {
    store : PropTypes.object
  };

  static propTypes = {
    results : PropTypes.object,
    query : PropTypes.object.isRequired,
    blueprint : PropTypes.object,
    onEvent : PropTypes.func.isRequired,
    seriesKey : PropTypes.string
  };

  static defaultProps = {
    results : { items : [] },
    seriesKey : null,
    blueprint : null
  };

  static getAccessFromBlueprint( geoPointPath, searchStrings, blueprint, query, def ) {
    const attributes = Path( 'data/attributes' ).get( blueprint, [] )
    for ( let j = 0; j < attributes.length; j++ ) {
      if ( attributes[j].type === 'geoPoint' ) {
        return {
          accessor : Path( `data/${attributes[j].path}/${geoPointPath}` ),
          value : `data/${attributes[j].path}/${geoPointPath}`
        }
      }
    }
    if ( query.paths == null || query.paths.length < 1 ) {
      const attributes = Path( 'data/attributes' ).get( blueprint, [] )
      for ( let i = 0; i < searchStrings.length; i++ ) {
        for ( let j = 0; j < attributes.length; j++ ) {
          if ( attributes[j].path.split( '/' ).includes( searchStrings[i] ) ) {
            return {
              accessor : Path( `data/${attributes[j].path}` ),
              value : `data/${attributes[j].path}`
            }
          }
        }
      }
    } else if ( query.paths && query.paths.length > 1 ) {
      const { paths } = query
      for ( let i = 0; i < searchStrings.length; i++ ) {
        for ( let j = 0; j < paths.length; j++ ) {
          const path = typeof paths[j] === 'string' ? paths[j] : paths[j][0]
          const alias = ( typeof paths[j] === 'string' || !paths[j][1] ) ? MapResults.getDefaultAlias( path ) : paths[j][1]
          if ( path.split( '/' ).includes( searchStrings[i] ) ) {
            return {
              accessor : Path( alias ),
              value : alias
            }
          }
        }
      }
    }
    return def
  }

  static getDefaultAlias( path ) {
    return path.split( '/' ).slice( -1 )[0]
  }

  static pointProperties( source ) {
    return source
  }

  static getDerivedStateFromProps( props, state ) {
    const {
      blueprint,
      query,
      results, 
      seriesKey, 
      selected, 
      rollover,
      targeted,
      geoshapeSelected,
      mapMode
    } = props

    const {
      geoshapes,
      symbols,
      itemSymbolizer,
      drawControl,
      historyLayer,
      itemLayer,
      series : oldSeries,
      latAccess : oldLatAccess,
      lonAccess : oldLonAccess
    } = state

    const latAccess = MapResults.getAccessFromBlueprint(
      'lat',
      [ 'latitude', 'lat' ],
      blueprint,
      query,
      oldLatAccess || {
        accessor : Path( 'lat' ),
        value : 'lat'
      }
    )
    const lonAccess = MapResults.getAccessFromBlueprint(
      'lon',
      [ 'longitude', 'lng', 'lon' ],
      blueprint,
      query,
      oldLonAccess || {
        accessor : Path( 'lon' ),
        value : 'lon'
      }
    )

    let filtered
    if ( results && results.items ) {
      filtered = results.items.filter( ( item ) => {
        return latAccess.accessor.get( item ) && lonAccess.accessor.get( item )
      } )
    }

    let series
    if ( seriesKey ) {
      const toReturn = {}
      filtered.forEach( ( item ) => {
        if ( !toReturn[item[seriesKey]] ) {
          toReturn[item[seriesKey]] = []
        }
        toReturn[item[seriesKey]].push( item )
      } )
      series = toReturn
    } else {
      series = { all : filtered }
    }

    let selectedComponent = null
    if ( selected && selected.length > 0 ) {
      const item = MapResults.getMapItemFromKey( series, selected[0] )
      if ( item ) {
        selectedComponent = (
          <div className={style.selected}>
            <pre>
              {item ? JSON.stringify( item, null, 2 ) : ''}
            </pre>
          </div>
        )
      }
    }

    let rolloverComponent = null
    if ( rollover && rollover.length > 0 ) {
      const item = MapResults.getMapItemFromKey( series, rollover[0] )
      if ( item ) {
        rolloverComponent = (
          <div className={style.rollover}>
            <pre>
              {item ? JSON.stringify( item, null, 2 ) : ''}
            </pre>
          </div>
        )
      }
    }

    const gsf = geoshapes.getFeatureCollectionFor( geoshapeSelected )
    const dc = (
      <DrawControl 
        key="cd"
        mapName={MAP_NAME}
        ref={drawControl}
        mode={mapMode}
        features={gsf}
        selectedIds={geoshapeSelected} />
    )

    let same = true
    if ( series ) {
      const oldKeys = new Set( ( oldSeries && Object.keys( oldSeries ) ) || [] )
      const keys = Object.keys( series )
      same = keys.every( k => oldKeys.has( k ) )
    }
    if ( oldLatAccess?.value !== latAccess?.value || oldLonAccess?.value !== lonAccess?.value ) {
      same = false
    }

    let newHistoryLayer = historyLayer, newItemLayer = itemLayer
    if ( !same || newHistoryLayer == null ) {
      const circlePaint = MapResults.getCirclePaint( 4, Object.keys( series ), seriesKey )
      const rolloverCirclePaint = MapResults.getCirclePaint( 10, Object.keys( series ), seriesKey )

      newHistoryLayer = new PolylineLayer( {
        dataType : 'item',
        latAccess : MapResults.getAccessor( latAccess ),
        lonAccess : MapResults.getAccessor( lonAccess ),
        pointProperties : MapResults.pointProperties,
        circlePaint,
        rolloverCirclePaint
      } )
    }
    if ( !same || newItemLayer == null ) {
      const itemCirclePaint = MapResults.getCirclePaint( 7, Object.keys( series ), seriesKey )
      const itemRolloverCirclePaint = MapResults.getCirclePaint( 12, Object.keys( series ), seriesKey )
      newItemLayer = new PolylineLayer( {
        dataType : 'recent',
        latAccess : MapResults.getAccessor( latAccess ),
        lonAccess : MapResults.getAccessor( lonAccess ),
        pointProperties : MapResults.pointProperties,
        circlePaint : itemCirclePaint,
        rolloverCirclePaint : itemRolloverCirclePaint
      } )
    }
    
    const seriesColors = MapResults.buildPolyLineSeriesOptions( series )
    const layers = symbols == null ? [] : [
      geoshapes.getLayers(),
      newHistoryLayer.getLayers( seriesColors, series, { rollover, selected, targeted } ),
      dc
    ]
    
    if ( symbols && series ) {
      const recent = {}
      Object.keys( series ).forEach( ( seriesId ) => {
        if ( series[seriesId][0] ) {
          recent[seriesId] = [ series[seriesId][0] ]
        }
      } )
      layers.push( newItemLayer.getLayers( seriesColors, recent, { selected, targeted, rollover } ) )
    }

    return {
      latAccess, 
      lonAccess,
      filtered, 
      series, 
      selectedComponent,
      rolloverComponent,
      layers,
      itemLayer : newItemLayer,
      historyLayer : newHistoryLayer
    }
  }

  static colors = [
    '#FF0000',
    '#00FF00',
    '#0000FF',
    '#39393D',
    '#E7431E',
    '#225AA2',
    '#25B7B7',
    '#15A7A7',
    '#059797',
    '#5737BE',
    '#F4F6FA',
    '#EEC755',
    '#C51A67'
  ]

  static getCirclePaint( radius, seriesSet, seriesKey ) {
    const stops = seriesSet.map( seriesId => [ seriesId, MapResults.getPolyLineSeriesColor( seriesId ) ] )

    let circleColor = { 
      property : seriesKey || '___PLACEHOLDER___',
      type : 'categorical',
      stops
    }
    if ( stops.length > 1 ) {
      stops.push( [ 'default', '#FF0000' ] )
    } else {
      circleColor = ( stops[0] && stops[0][1] ) || '#FF0000'
    }

    return {
      'circle-radius' : radius,
      'circle-color' : circleColor,
      'circle-opacity' : 1
    }
  }

  static buildPolyLineSeriesOptions( series ) {
    const options = {}
    Object.keys( series ).forEach( ( seriesId ) => {
      options[seriesId] = { color : MapResults.getPolyLineSeriesColor( seriesId ) }
    } )
    return options
  }

  static getPolyLineSeriesColor( seriesId ) {
    const toRemain = parseInt( Buffer.from( seriesId.slice( -5 ) ).toString( 'hex' ), 16 )
    const index = toRemain % 13
    return MapResults.colors[index]
  }

  static getMapItemFromKey( series, key ) {
    const splitId = ( key && key.split( '-' ) ) || []
    const index = splitId[2]
    const seriesId = splitId[1]

    if ( index && seriesId && series && series[seriesId] && series[seriesId][index] ) {
      return series[seriesId][index]
    }
    return null
  }

  static getAccessor( access ) {
    return ( data, def ) => {
      let val = access && access.accessor.get( data, def )
      if ( typeof val === 'string' ) {
        try {
          val = Number( val )
        } catch ( ex ) {
          val = null
        }
      }
      return val
    }
  }

  static TYPE = 'map';

  constructor( props ) {
    super( props )

    this.state = {
      latAccess : null,
      lonAccess : null,
      showAccessSettings : false,
      drawControl : React.createRef(),
      symbols : SymbolSet.create( s => this.setState( { symbols : s.getImagesArray() } ) ),
      geoshapes : new GeoshapeController( { map : this } ),
      mapOptions : new ReduxMapOptions( this, MAP_NAME, { UI } )
    }
  }

  latAccessor = ( data, def ) => {
    const { latAccess } = this.state
    let val = latAccess && latAccess.accessor.get( data, def )
    if ( typeof val === 'string' ) {
      try {
        val = Number( val )
      } catch ( ex ) {
        val = null
      }
    }
    return val
  }

  lonAccessor = ( data, def ) => { 
    const { lonAccess } = this.state
    let val = lonAccess && lonAccess.accessor.get( data, def )
    if ( typeof val === 'string' ) {
      try {
        val = Number( val )
      } catch ( ex ) {
        val = null
      }
    }
    return val
  }

  /**
   * Called on Selection. 
   **/
  onMapClick = ( e ) => {
    const { store } = this.context

    if ( e.type == null ) {
      store.dispatch( Select.set( null, 'selected' ) )
    } else if ( e.type === 'item' || e.type === 'recent' ) {
      store.dispatch( Select.set( e.id, 'selected' ) )      
    }
  }

  fitBounds = () => {
    const { filtered } = this.state
    if ( filtered && filtered.length > 1 ) {
      let maxLat = -200, maxLon = -200, minLat = 200, minLon = 200
      filtered.forEach( ( item ) => {
        const lat = this.latAccessor( item, null )
        const lon = this.lonAccessor( item, null )
        if ( lat > maxLat ) {
          maxLat = lat
        }
        if ( lat < minLat ) {
          minLat = lat
        }
        if ( lon > maxLon ) {
          maxLon = lon
        }
        if ( lon < minLon ) {
          minLon = lon
        }
      } )
      MapControl.flyToBounds( MAP_NAME, { north : maxLat, south : minLat, east : maxLon, west : minLon } )
    }
  }

  onRollover = ( e ) => {
    const { rollover } = this.props
    const { store } = this.context

    // Is rollover the same?
    if ( ( e.id == null && Select.isEmpty( rollover ) ) || Select.isSelected( rollover, e.id ) ) {
      return
    }
    const id = typeof e.id === 'undefined' ? null : e.id
    store.dispatch( Select.set( id, 'rollover' ) )
  }

  canLoadMore = () => {
    const { results } = this.props
    const maxRange = results && results.ranges && results.ranges[results.ranges.length - 1] && results.ranges[results.ranges.length - 1][1]
    const itemLength = results && results.items && results.items.length
    
    return maxRange === itemLength
  }

  isLoading = () => {
    const { results } = this.props
    return results.status === ReduxConstants.QUERY_STATUS_LOADING
  }

  loadMore = () => {
    const { onEvent } = this.props
    onEvent( { type : 'loadMore' }, 'select' )
  }

  handleLat = ( evt ) => {
    this.setState( {
      latAccess : {
        accessor : Path( evt.value ),
        value : evt.value
      }
    } )
  }

  handleLon = ( evt ) => {
    this.setState( { 
      lonAccess : {
        accessor : Path( evt.value ),
        value : evt.value
      }
    } )
  }

  onSettings = () => {
    this.setState( state => ( { showAccessSettings : !state.showAccessSettings } ) )
  }

  componentDidMount() {
    this.fitBounds()
  }

  getMostRecentIndex = () => {
    const { query } = this.props
    if ( query.sort ) {
      query.sort.forEach( ( sortBy ) => {
        if ( typeof sortBy === 'string' ) {
          
        }
      } )
    }
  }

  render() {
    const {
      mapStyle, 
      initialPosition
    } = this.props

    const {
      symbols,
      latAccess, 
      lonAccess, 
      showAccessSettings, 
      selectedComponent,
      rolloverComponent,
      layers,
      drawControl,
      mapOptions,
      geoshapes
    } = this.state

    return (
      <div className={style.mapResults}>
        <Map
          mapName={MAP_NAME}
          mapOptions={mapOptions}
          mapStyle={MapStyle.url( mapStyle )}
          initialPosition={initialPosition}
          symbols={symbols}
          layers={layers}
          drawControl={drawControl.current}
          onClick={this.onMapClick}
          onRollover={this.onRollover}
          onDrawCreate={geoshapes.onCreateShape}
          onDrawCombine={geoshapes.onCombineShapes}
          onDrawUpdate={geoshapes.onShapeUpdate}
          onDrawModeChange={geoshapes.onModeChange}
          onMapReady={mapOptions.onMapReady} />
        <div className={style.controls}>
          <Button className={style.mapStyle} title="change map style" onClick={( evt ) => { mapOptions.onNextMapStyle( evt ); return false }}><i className="fa fa-globe" /></Button>
          <Button className={style.settings} title="open map settings" onClick={this.onSettings}><i className="fa fa-sliders" /></Button>
          <Button className={style.bound} title="set bounding box" onClick={this.fitBounds}>
            <div className={classnames( style.top, style.left )} />
            <div className={classnames( style.top, style.right )} />
            <div className={classnames( style.bottom, style.left )} />
            <div className={classnames( style.bottom, style.right )} />
          </Button>
          { this.canLoadMore() ? <Button title="load more data" className={style.loadMore} onClick={this.loadMore}><i className={this.isLoading() ? classnames( 'fa fa-spinner', style.loading ) : 'fa fa-ellipsis-h'} /></Button> : null }
        </div>
        <div className={classnames( style.accessSettings, showAccessSettings ? '' : style.hidden )}>
          <LabelGroup className={style.setting}>
            <label>lat path: </label>
            <TextInput className={style.input} onChange={this.handleLat} value={latAccess.value} />
          </LabelGroup>
          <LabelGroup className={style.setting}>
            <label>lon path: </label>
            <TextInput className={style.input} onChange={this.handleLon} value={lonAccess.value} />
          </LabelGroup>
        </div>
        {selectedComponent}
        {rolloverComponent}
      </div>
    )
  }
}

exports.MapResults = connect( ( state ) => {
  const selected = state.select.selected || []
  const targeted = state.select.targeted || []

  return {
    targeted,
    selected,
    rollover : state.select.rollover,

    geoshapes : [],
    geoshapesActive : state.ui.geoshapesActive,
    geoshapeSelected : [],
   
    mapMode : state.ui.map.mode,
    mapStyle : ReduxMapOptions.getMapStyle( state, MAP_NAME ),
    initialPosition : ReduxMapOptions.getInitialPosition( state, MAP_NAME )
  }
} )( MapResults )
